import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, {
  Extrapolation,
  FadeIn,
  FadeOut,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { useStore } from '../../../contexts/StoreContext';
import { observer } from 'mobx-react-lite';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { neutrals } from 'src/theme/colors';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { sliderRotationTestID } from 'src/helpers/testIDs.helper';
import { styles } from './SliderRotation.styles';
import { IconButton } from '@photosi/rn-components/buttons/icon-button/IconButton';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { emit } from '../../../contexts/TransformEventBus';
import { runOnUI } from 'react-native-reanimated';
import { withActiveUserImage } from 'src/hoc/withActiveUserImage';
import { UserImageModelInstance } from 'src/store/_models/userImage';
import { parseAnalyticsParamsByUserImage } from 'src/_shared/analytics.utils';
const MAX_VALUE: number = 180;
const MIN_VALUE: number = -180;

const BACKGROUND_COLOR = neutrals.coldGrays.pearl;

const wrapAngle = (value: number, MIN_VALUE: number, MAX_VALUE: number): number => {
  if (value > MAX_VALUE) {
    // Se il valore supera il massimo, somma l'eccesso al minimo
    return MIN_VALUE + (value - MAX_VALUE);
  } else if (value < MIN_VALUE) {
    // Se il valore è inferiore al minimo, sottrae la differenza dal massimo
    return MAX_VALUE - (MIN_VALUE - value);
  } else {
    // Se il valore è già nell'intervallo, restituisci il valore stesso
    return value;
  }
};

const ANGLES = Array.from({ length: 361 }, (_, i) => i - 180);

export type SliderRotationProps = {
  activeUserImage: UserImageModelInstance;
  onChange?: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  gradientColor?: string;
  startFocusOrder?: number;
};

/**
 * SliderRotation - Modalità Hybrid
 *
 * Questo componente ora opera in modalità hybrid seguendo lo stesso pattern di SliderZoom:
 * 1. COMPORTAMENTO ESISTENTE: Mantiene tutta la logica originale invariata
 *    - userImage.setRotation() per stati temporanei durante drag
 *    - userImage.applyRotation() per applicazione finale
 *    - Callbacks onChange/onChangeEnd funzionano identicamente
 *    - Gesture pan e button controls ±90° inalterati
 *
 * 2. NUOVO: TransformEventBus integration
 *    - Emette eventi 'temp-rotation' durante drag e button press
 *    - Emette eventi 'apply-rotation' al termine delle operazioni
 *    - Zero impatto sulla funzionalità esistente
 *    - Preparazione per provider locali con eventi rotation
 *
 * La funzionalità è identica a prima, ma ora è pronta per il sistema eventi.
 */

const SliderRotationComponent = observer(
  ({
    activeUserImage,
    onChange = () => {},
    onChangeEnd = () => {},
    gradientColor = BACKGROUND_COLOR,
    startFocusOrder = 0,
  }: SliderRotationProps) => {
    const { i18n } = useStore();

    const initialValue = activeUserImage.rotationDeg ?? 0;
    const [angleValue, setAngleValue] = React.useState<number>(initialValue);
    const [isHoveredIncrease, setIsHoveredIncrease] = useState(false);
    const [isHoveredDecrease, setIsHoveredDecrease] = useState(false);

    useEffect(() => {
      setAngleValue(initialValue);
    }, [initialValue]);

    const ref = React.useRef<HTMLDivElement>();
    const translateXSV = useSharedValue(0);
    const prevTranslateXSV = useSharedValue(0);

    const minPositionX = 109;
    const [maxPositionX, setMaxPosition] = React.useState(0);

    const getTranslateXByAngle = (angle: number) => {
      return interpolate(angle, [MIN_VALUE, MAX_VALUE], [minPositionX, maxPositionX]);
    };

    const getAngleFromTranslateX = (translateX: number) => {
      return interpolate(translateX, [minPositionX, maxPositionX], [MIN_VALUE, MAX_VALUE], Extrapolation.IDENTITY);
    };

    useEffect(() => {
      translateXSV.value = getTranslateXByAngle(angleValue);
    }, [angleValue, maxPositionX]);

    useEffect(() => {
      if (!ref.current) return;
      const maxPosition = -(ref.current?.getBoundingClientRect().width || 0) + 111;
      setMaxPosition(maxPosition);
      translateXSV.value = getTranslateXByAngle(angleValue);
    }, [ref.current]);

    const pan = Gesture.Pan()
      .minDistance(1)
      .onStart(() => {
        prevTranslateXSV.value = translateXSV.value;
      })
      .onChange(event => {
        const newX = prevTranslateXSV.value + event.translationX * 2;

        /**
         * limits newX value into min/max values based on translation
         * e.g. 210 will be transformed into 180
         */
        if (newX > minPositionX) {
          translateXSV.value = minPositionX;
        } else if (newX < maxPositionX) {
          translateXSV.value = maxPositionX;
        } else {
          translateXSV.value = newX;
        }

        const angle = getAngleFromTranslateX(translateXSV.value);

        setAngleValue(angle);
        onChange(angle);

        // activeUserImage.setRotation(angle, responsiveScale);

        // NUOVO: Emit evento temporaneo direttamente da worklet (UI thread)
        emit({
          type: 'temp-rotation',
          userImageId: activeUserImage.id,
          payload: { absoluteRotation: angle },
          source: 'slider',
        });
      })
      .onEnd(() => {
        amplitude.trackEvent(AnalyticsEvents.modifyPhoto.rotateClick, {
          ...parseAnalyticsParamsByUserImage(activeUserImage),
          type: 'slider',
          value: angleValue.toFixed(0),
        });
        setAngleValue(angleValue);
        onChangeEnd(angleValue);

        // activeUserImage.applyRotation(angleValue, responsiveScale);

        // NUOVO: Emit evento apply direttamente da worklet (UI thread)
        emit({
          type: 'apply-rotation',
          userImageId: activeUserImage.id,
          payload: { absoluteRotation: angleValue },
          source: 'slider',
        });
      });

    const animatedStyles = useAnimatedStyle(() => {
      return {
        position: 'absolute',
        flexDirection: 'row',
        gap: 10.08,
        transform: [{ translateX: translateXSV.value }],
      };
    });

    const rotateToNearest90 = (reverse: boolean) => {
      const current = angleValue;

      let angle;
      // Verifica se l'angolo corrente è già un multiplo di 90
      if (current % 90 === 0) {
        // Se reverse è true, vai al prossimo multiplo di 90
        if (!reverse) {
          // Se l'angolo è già un multiplo di 90, passiamo al successivo
          angle = current + 90;
        } else {
          // Se reverse è false, vai al precedente multiplo di 90
          angle = current - 90;
        }
      } else {
        // Se l'angolo corrente non è multiplo di 90 calcolo il multiplo di 90 più vicino
        if (!reverse) {
          // Se reverse è false, arrotonda al multiplo più vicino
          angle = Math.round(current / 90) * 90; // Arrotonda al più vicino
        } else {
          // Se reverse è true, arrotonda verso il multiplo di 90 inferiore
          angle = Math.floor(current / 90) * 90; // Arrotonda verso il basso
        }
      }

      // Limita l'angolo al range specificato
      const next = wrapAngle(angle, MIN_VALUE, MAX_VALUE);

      // Esegui l'animazione della rotazione
      const newTranslateX = getTranslateXByAngle(next);
      translateXSV.value = withSpring(newTranslateX);

      const newAngle = Math.round(next);

      // Aggiorna il valore dell'angolo e invoca il callback
      setAngleValue(newAngle);
      onChangeEnd(newAngle);

      // activeUserImage.applyRotation(newAngle, responsiveScale);

      // NUOVO: Emit eventi per button controls (Main Thread)
      runOnUI(() => {
        emit({
          type: 'apply-rotation',
          userImageId: activeUserImage.id,
          payload: { absoluteRotation: newAngle },
          source: 'slider',
        });
      })();

      return newAngle;
    };

    return (
      <View style={styles.container} testID={sliderRotationTestID()}>
        {/* ROTATE LEFT BUTTON */}
        <IconButton
          onPress={async () => {
            const newAngle = rotateToNearest90(true);
            amplitude.trackEvent(AnalyticsEvents.modifyPhoto.rotateClick, {
              ...parseAnalyticsParamsByUserImage(activeUserImage),
              type: 'button',
              value: newAngle,
            });
          }}
          testID={sliderRotationTestID(TestIDs.ROTATE_PREV)}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:rotate_left`)}
          accessibilityRole="button"
          style={styles.buttonRotate}
          focusStyle={styles.buttonRotateFocus}
          focusId={sliderRotationTestID(TestIDs.ROTATE_PREV)}
          focusOrder={startFocusOrder}
          focusGroup={FocusGroups.AFTER_PRODUCT}
          iconName={ICON_NAMES.RotateSx}
          iconStyle={styles.icon}
          onHoverIn={() => setIsHoveredDecrease(true)}
          onHoverOut={() => setIsHoveredDecrease(false)}
        />
        {isHoveredDecrease && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={styles.hoverTextContainer}
            pointerEvents="none"
          >
            <TextLato style={styles.hoverText}>{i18n.t(`actions:rotate_left`)}</TextLato>
          </Animated.View>
        )}
        <GestureHandlerRootView>
          <GestureDetector gesture={pan}>
            <View style={styles.container_gesture_handler}>
              <Animated.View style={styles.central_pin_container}>
                <TextLato testID={sliderRotationTestID(TestIDs.ANGLE)} style={styles.central_number}>
                  {Math.round(angleValue)}°
                </TextLato>
                <Animated.View style={styles.central_pin} />
              </Animated.View>
              <LinearGradient
                colors={['#f3f7f700', gradientColor]}
                start={{ x: 1, y: 0 }}
                end={{ x: 0, y: 0 }}
                style={[styles.linear_gradient, styles.linear_gradient_right]}
              />
              <LinearGradient
                colors={['#f3f7f700', gradientColor]}
                start={{ x: 1, y: 0 }}
                end={{ x: 0, y: 0 }}
                style={[styles.linear_gradient, styles.linear_gradient_left]}
              />
              <Animated.View style={[styles.slide]}>
                <Animated.View style={[animatedStyles]} ref={ref}>
                  {ANGLES.map(angle => {
                    return (
                      <Animated.View
                        key={angle}
                        style={[styles.pin, (angle == 0 || Math.abs(angle) == 180) && { borderColor: 'red' }]}
                      />
                    );
                  })}
                </Animated.View>
              </Animated.View>
            </View>
          </GestureDetector>
        </GestureHandlerRootView>
        {/* ROTATE RIGHT BUTTON */}
        <IconButton
          onPress={async () => {
            const newAngle = rotateToNearest90(false);
            amplitude.trackEvent(AnalyticsEvents.modifyPhoto.rotateClick, {
              ...parseAnalyticsParamsByUserImage(activeUserImage),
              type: 'button',
              value: newAngle,
            });
          }}
          testID={sliderRotationTestID(TestIDs.ROTATE_NEXT)}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:rotate_right`)}
          accessibilityRole="button"
          style={styles.buttonRotate}
          focusStyle={styles.buttonRotateFocus}
          focusId={sliderRotationTestID(TestIDs.ROTATE_NEXT)}
          focusOrder={startFocusOrder + 1}
          focusGroup={FocusGroups.AFTER_PRODUCT}
          iconName={ICON_NAMES.RotateDx}
          iconStyle={styles.icon}
          onHoverIn={() => setIsHoveredIncrease(true)}
          onHoverOut={() => setIsHoveredIncrease(false)}
        />
        {isHoveredIncrease && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={styles.hoverTextContainerRight}
            pointerEvents="none"
          >
            <TextLato style={styles.hoverText}>{i18n.t(`actions:rotate_right`)}</TextLato>
          </Animated.View>
        )}
      </View>
    );
  },
);

export const SliderRotation = withActiveUserImage(SliderRotationComponent);
